import { store } from "@/store"
import {
  addresssSuggest,
  addToCart,
  discount,
  orderSubmit,
  setCart,
  setCartCount,
  getCart,
} from "@/store/features/cartSlice"
import { getQuantityCount, deleteFromCart } from "@/utils/cart"
import { getShortenedUrlPath } from "@/utils/helper"
import { checkExpPayPal } from "@/utils/payment"

import { FRAUD_DECLINED_MESSAGE_ENDPOINT } from "@/constants/routes"
import {
  GLOBAL_CONFIGURATION_ENDPOINT,
  GENERIC_ERROR_MESSAGE,
} from "@/constants/"
import { aemAxiosInstance } from "@/constants/api"
import {
  setPaymentFailedAction,
  setPaymentErrorMessageAction,
  setPaymentTechnicalFailedAction,
  setIsPaymentInfoAction,
} from "@/store/features/checkoutSlice"

const getGeoLocationDataFromCookie = () => {
  const userLocation =
    document.cookie.match("(^|;)\\s*userLocation\\s*=\\s*([^;]+)")?.pop() || ""
  let geoLocationData = {}
  if (userLocation !== "") {
    const splitLocation = userLocation.split("+")
    if (splitLocation[4] && splitLocation[5]) {
      geoLocationData = {
        lat: splitLocation[4],
        lng: splitLocation[5],
      }
    }
  }
  return geoLocationData
}

export const getAddressSuggestions = async (addr, useGeoLocation = false) => {
  if (!addr || addr.trim() === "") return []
  try {
    let geoLocationData = {}
    // If useGeoLocation is true, get the geo location data from cookie
    if (useGeoLocation) {
      geoLocationData = getGeoLocationDataFromCookie()
    }
    const res = await store.dispatch(addresssSuggest({ addr, geoLocationData }))
    if (res && res.payload && res.payload.results) {
      return res.payload.results.map(item => item.suggestion ?? "")
    }
    return []
  } catch (err) {
    console.log("Failed to get suggestions from Experian", err)
    throw err
  }
}

export const refreshCart = async () => {
  const cart = store?.getState()?.cart?.cart
  const { lineItems = [], taxedPrice = {} } = cart

  if (lineItems?.length === 0) return
  const shippingZip = localStorage.getItem("shippingZip")

  let payload = {}
  if (shippingZip && shippingZip.trim() !== "" && !taxedPrice) {
    payload = {
      actions: [
        {
          action: "recalculate",
          updateProductData: true,
        },
        {
          action: "calculateTax",
        },
      ],
    }
  } else {
    payload = {
      actions: [
        {
          action: "recalculate",
          updateProductData: true,
        },
      ],
    }
  }
  await store
    .dispatch(addToCart(payload))
    .unwrap()
    .then(res => {
      if (res) {
        store.dispatch(setCart(res))
        const quantityLineItems = res?.lineItems
        const payload = getQuantityCount(quantityLineItems)
        store.dispatch(setCartCount(payload))
      }
    })
}

export const checkInactivePromos = async (cb, cb1, isModalOpen = false) => {
  const actions = []
  let actionData
  const cart = store?.getState()?.cart?.cart
  const { discountCodes = [] } = cart
  discountCodes
    .filter(item => item.state !== "MatchesCart")
    .map(item => {
      actions.push({
        action: "removeDiscountCode",
        discountCode: { typeId: "discount-code", id: item.discountCode?.id },
      })
      actionData = [...actions]
    })
  if (actionData?.length > 0) {
    store
      .dispatch(discount({ actions: actionData }))
      .unwrap()
      .then(res => {
        if (res && res.type === "Cart") {
          store
            .dispatch(addToCart({ actions: [{ action: "calculateTax" }] }))
            .unwrap()
            .then(resp => {
              if (resp && resp.type === "Cart") {
                store.dispatch(setCart(res))
                store.dispatch(setCartCount(res.lineItems))
                createOrder(cb, cb1, isModalOpen)
              }
            })
        }
      })
  } else {
    createOrder(cb, cb1, isModalOpen)
  }
  return actionData
}

export const createOrder = async (cb, cb1, isModalOpen = false) => {
  store.dispatch(setPaymentErrorMessageAction(""))
  store.dispatch(setPaymentFailedAction(false))
  const paymentId = store.getState()?.cart?.paymentId
  const { checkout: { paymentMethodName = "" } = {} } = store.getState()

  const {
    generic: {
      globalConfig: {
        general: {
          siteName = "",
          orderConfirmPath = "/",
          homepageUrl = "/",
        } = {},
      } = {},
    } = {},
  } = store.getState()

  const orderConfirmUrl = getShortenedUrlPath(orderConfirmPath)
  const homePagePath = getShortenedUrlPath(homepageUrl)

  const { IGLOO: { getBlackbox } = {} } = window || {}
  const { blackbox, finished } = getBlackbox?.() || {}

  const payload = {
    paymentId: paymentId,
    orderSource: siteName,
  }

  if (finished) {
    payload.deviceId = encodeURIComponent(blackbox ?? "")
  }

  store
    .dispatch(orderSubmit(payload))
    .unwrap()
    .then(res => {
      const {
        parentOrder: { orderNumber = "", customerEmail: email = "" },
      } = res
      if (res) {
        const base64Str = Buffer.from(email).toString("base64")
        const redirectionLink = `${orderConfirmUrl}?orderNumber=${orderNumber}${"&q=".concat(
          base64Str
        )}`
        cb1 && cb1(redirectionLink, isModalOpen, paymentMethodName)
        window.history.replaceState(null, null, homePagePath)
        window.location.href = redirectionLink
      } else {
        cb && cb(false) // enable Place Order CTA on failure
      }
      store.dispatch(setPaymentFailedAction(false))
    })
    .catch(error => {
      const { error: errorMsg, paymentCompleted = false } = error
      store.dispatch(setPaymentFailedAction(true))
      if (paymentCompleted) {
        store.dispatch(setPaymentTechnicalFailedAction(true))
        store.dispatch(setIsPaymentInfoAction(true))
        // popup condition
      } else if (errorMsg === "Declined") {
        deleteFromCart()
        window.location.href = FRAUD_DECLINED_MESSAGE_ENDPOINT
      } else {
        store
          .dispatch(getCart())
          .unwrap()
          .then(async () => {
            await refreshCart()
            callErrorMessge(error)
          })
        cb && cb(false)
      }
    })
}

const callErrorMessge = async error => {
  const {
    generic: {
      aciPaymentErrorMessagesPath = "",
      globalConfig: { general: { checkoutPagePath = "/" } = {} } = {},
    } = {},
    checkout: { cardLastDigit = "" } = {},
  } = store.getState()

  const isXpp = checkExpPayPal()
  const checkoutUrl = getShortenedUrlPath(checkoutPagePath)

  if (isXpp) {
    window.history.replaceState(null, null, checkoutUrl)
  }

  const { error: errorMsg = "", errorCode } = error

  try {
    const { data } = await aemAxiosInstance.get(
      GLOBAL_CONFIGURATION_ENDPOINT + "?path=" + aciPaymentErrorMessagesPath
    )
    const { value: authorErrorMsg = errorMsg } =
      data.find(e => e.text === errorCode) || {}

    const errorMessage = authorErrorMsg.replace(
      "card-last-4-digits",
      cardLastDigit
    )

    // payment  section open error condition
    store.dispatch(
      setPaymentErrorMessageAction(errorMessage || GENERIC_ERROR_MESSAGE)
    )
  } catch (error) {
    // payment  section open error condition
    const errorMsgNew = errorMsg.replace("card-last-4-digits", cardLastDigit)
    store.dispatch(
      setPaymentErrorMessageAction(errorMsgNew || GENERIC_ERROR_MESSAGE)
    )
  }
}

export const getEventInfo = (type, link, needJson = false) => {
  const eventInfo = {
    eventAction: "checkout:review:" + type,
    eventName: "checkout:review:" + type,
    eventType: "navigation",
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: type,
    internalLinkPosition: "checkout",
    internalLinkType: "checkout:navigation",
    internalLinkZoneName: "checkout:review",
    internalLinkURL: link
      ? (link.indexOf("http") === -1 ? window.location.origin : "") + link
      : "n/a",
    clickInternalLinks: "true",
  }
  if (needJson) {
    return eventInfo
  }
  return JSON.stringify(eventInfo)
}

export const getShippingAddressFormated = data => {
  const {
    city = "",
    country = "",
    email = "",
    firstName = "",
    lastName = "",
    phone = "",
    postalCode = "",
    state = "",
    streetName = "",
    streetNumber = "",
    id = "",
  } = data

  return {
    fname: {
      value: firstName,
      error: false,
      message: "",
    },
    lname: {
      value: lastName,
      error: false,
      message: "",
    },
    email: {
      value: email,
      error: false,
      message: "",
    },
    phone: {
      value: phone,
      error: false,
      message: "",
    },
    address1: {
      value: streetName,
      error: false,
      message: "",
    },
    address2: streetNumber,
    city: {
      value: city,
      error: false,
      message: "",
    },
    state: {
      value: state,
      error: false,
      message: "",
    },
    zipcode: {
      value: postalCode,
      error: false,
      message: "",
    },
    country: {
      value: country,
      error: false,
      message: "",
    },
    notes: "",
    label: {
      show: true,
      value: "",
    },
    id: id,
  }
}
